import { useRef } from 'react';
import {
  computeEtaState,
  windowRatePerMin,
  ETA_WINDOW_SEC,
  type EtaSample,
  type EtaState,
} from '@/lib/optimizationEta';

/**
 * Sammelt {t, completed}-Messpunkte fuer die laufende Optimierung und leitet
 * daraus ETA + Fensterrate ab (siehe `lib/optimizationEta.ts` fuer die drei
 * Messfehler, die das ersetzt).
 *
 * Samples liegen in einem Ref statt in State: sie sind ein Messprotokoll,
 * kein eigener Renderanlass — das Modal treibt seinen 1s-Ticker ohnehin
 * (siehe `OptimizationProgressModal.tsx`), der reicht als Render-Trigger.
 * Ein State-Update pro Sample wuerde nur einen zusaetzlichen Render pro
 * ~900ms erzeugen, ohne dass irgendein Consumer diesen einzelnen Tick
 * braucht.
 *
 * Diese Ref-Schreibzugriffe laufen bewusst waehrend des Renders, nicht in
 * einem Effect — sicher ist das nur, weil diese App OHNE `React.StrictMode`
 * laeuft (kein Doppel-Invoke der Render-Funktion in Dev) UND weil die
 * 900ms-Sample-Schranke unten selbst einen Doppel-Aufruf mit (nahezu)
 * identischem `now` idempotent macht (der zweite Aufruf haengt hoechstens
 * denselben Wert nochmal an oder ueberspringt ihn ganz). Kaeme StrictMode
 * dazu, muesste dieser Mechanismus neu geprueft werden.
 *
 * Reset-Regeln:
 *   - Job-Wechsel (`jobId` aendert sich): Samples + Messspanne verwerfen. Ein
 *     neuer Job hat eine neue Anlaufphase, die alte Rate ist irrelevant.
 *   - `completed` faellt gegenueber dem letzten Sample zurueck (Retry/Reset
 *     eines Batches): ebenfalls kompletter Neustart der Messreihe. Eine
 *     Rate, die aus einem Ruecksprung berechnet wuerde, waere negativ oder
 *     zufaellig klein — beides falsch.
 *   - `observedSinceMs` ist die eigene Beobachtungsspanne dieser Hook-Instanz,
 *     NICHT `optimization_jobs.started_at`. Ein spaet geoeffnetes Modal
 *     (Reload, zweiter Tab) hat den Anlauf des Jobs nie gesehen und darf sich
 *     dessen Rate nicht borgen (siehe Testfall "Modal spaet geoeffnet" in
 *     `optimizationEta.test.ts`).
 */
export function useOptimizationEta(
  jobId: string | null,
  completed: number,
  total: number,
  active: boolean,
): { eta: EtaState; ratePerMin: number | null } {
  const samplesRef = useRef<EtaSample[]>([]);
  const jobRef = useRef<string | null>(null);
  const observedSinceRef = useRef<number>(0);

  if (jobRef.current !== jobId) {
    jobRef.current = jobId;
    samplesRef.current = [];
    observedSinceRef.current = 0;
  }

  const now = Date.now();
  if (active && jobId) {
    const arr = samplesRef.current;
    const lastSample = arr[arr.length - 1];
    if (lastSample && completed < lastSample.completed) {
      arr.length = 0;
      observedSinceRef.current = 0;
    }
    if (arr.length === 0) observedSinceRef.current = now;
    // Ein Sample pro ~900ms reicht fuer die 120s-Fensterrate und haelt den
    // Puffer klein; das Modal rendert per 1s-Ticker ohnehin oefter, als hier
    // neue Punkte entstehen.
    if (!arr.length || now - arr[arr.length - 1].t >= 900) arr.push({ t: now, completed });
    // Puffer kappen: Fenster + Reserve reicht; observedSince liegt separat im
    // Ref und ueberlebt das Kappen unveraendert.
    const cutoff = now - (ETA_WINDOW_SEC + 30) * 1000;
    while (arr.length > 2 && arr[0].t < cutoff && arr[1].t < cutoff) arr.shift();
  }

  if (!active || !jobId) {
    return { eta: { kind: 'calculating' }, ratePerMin: null };
  }

  return {
    eta: computeEtaState(samplesRef.current, now, total, observedSinceRef.current),
    ratePerMin: windowRatePerMin(samplesRef.current, now),
  };
}
